import { Components } from "appworks/components/components";
import { gameState } from "appworks/model/game-state";
import { Model } from "appworks/model/model";
import { commsManager } from "appworks/server/comms-manager";
import { Services } from "appworks/services/services";
import { SoundEvent } from "appworks/services/sound/sound-events";
import { SoundService } from "appworks/services/sound/sound-service";
import { TransactionService } from "appworks/services/transaction/transaction-service";
import { State } from "appworks/state-machine/states/state";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { Sequence } from "appworks/utils/contracts/sequence";
import { FooterComponent } from "slotworks/components/footer/footer-component";
import { AbstractMatrixComponent } from "slotworks/components/matrix/abstract-matrix-component";
import { ReelSpinner } from "slotworks/components/matrix/reel/transition-behaviours/spin/reel-spinner";
import { FreespinRecord } from "slotworks/model/gameplay/records/freespin-record";
import { FreespinRequestPayload } from "slotworks/model/gameplay/requests/freespin-request-payload";
import { AutoplayService } from "slotworks/services/autoplay/autoplay-service";
import { JurisdictionService } from "slotworks/services/jurisdiction/jurisdiction-service";

export interface RequestFreespinStateConfig {
    /** Change this if using this state with a secondary matrix component */
    matrixComponentType: { new (...args: any[]): AbstractMatrixComponent };
}

export class RequestFreespinState extends State {
    protected config: RequestFreespinStateConfig = {
        matrixComponentType: AbstractMatrixComponent
    };
    protected skipping: boolean;

    constructor(config?: Partial<RequestFreespinStateConfig>) {
        super();

        if (config) {
            this.config = { ...this.config, ...config };
        }
    }

    public onEnter(cascadeSkip?: boolean) {
        this.skipping = cascadeSkip;

        if (this.skipping || !Services.get(JurisdictionService).getSpinSkippingEnabled()) {
            uiFlags.set(UIFlag.NO_SKIP, true);
        }

        uiFlags.set(UIFlag.IDLE, false);
        uiFlags.set(UIFlag.GAME_IN_PROGRESS, true);
        uiFlags.set(UIFlag.AWAITING_RESPONSE, true);

        Components.get(FooterComponent)?.showFreespinStart();

        ReelSpinner.disableMinSpinTime = true;

        new Sequence([
            () => this.startSpinning(),
            () => this.updateRecord()
        ]).then(() => {
            if (this.skipping) {
                this.skip();
            } else {
                this.complete();
            }
        });
    }

    public onExit() {
        uiFlags.set(UIFlag.AWAITING_RESPONSE, false);
    }

    public error() {
        Components.get(this.config.matrixComponentType).reset();
        Services.get(AutoplayService).stop();
        Model.write({ settings: { turboSpin: false } });

        super.error();
    }

    public onSkip() {
        this.skipping = true;
    }

    protected updateRecord(): Contract<void> {
        if (!gameState.getCurrentGame().getCurrentRecord()) {
            this.error();
        } else {
            gameState.getCurrentGame().nextRecord();
            // We need to run these wrapper updates on the up to date freespin records.
            const currentGame = gameState.getCurrentGame();
            const gameComplete = currentGame.isComplete();

            gameWrapper.balancesUpdate(
                currentGame.realBalance,
                currentGame.freeBalance,
                gameComplete ? currentGame.getTotalWin() : 0
            );
            gameWrapper.balancesUpdate(
                currentGame.realBalance,
                currentGame.freeBalance,
            );
        }

        return Contract.empty();
    }

    protected startSpinning(): Contract<any> {
        return new Parallel([
            () => this.startReels(),
            () => this.requestSpin()
        ]);
    }

    protected startReels() {
        const gameplay = gameState.getCurrentGame();

        Services.get(TransactionService).setBalance(gameplay.startBalance);

        const record = gameplay.getCurrentRecord();
        if (record && (record instanceof FreespinRecord || record.getFreespinsWon() > 0)) {
            Services.get(SoundService).event(SoundEvent.start_freespin);
        } else {
            Services.get(SoundService).event(SoundEvent.start_spin);
        }

        const settings = Model.read().settings;
        const quickSpin = settings.quickSpin || settings.turboSpin;

        return Components.get(this.config.matrixComponentType).startTransition(false, quickSpin);
    }

    protected requestSpin() {
        const payload = new FreespinRequestPayload();

        return commsManager.request(payload);
    }
}
